/**
 * EDIT — one selected section, switched through a horizontal section bar.
 *
 * WHAT STAYS ON SCREEN. The selection UI is the load-bearing half of this panel: the
 * waveform, the two timecode fields, the validation, the selected-duration readout, and the
 * presets all stay visible at the top while the user moves between Edit's subsections.
 * That is the point of the toggle bar below it — a new section opens without hiding the
 * range editor it is going to act on.
 *
 * WHAT IS REAL. Trim, Cut, Insert Silence and Fade In and Out all act on the song's stored
 * audio. Trim keeps only the selected section; Cut removes it and joins what remains; Insert
 * Silence splices a generated silent gap in at a chosen point; Fade ramps the volume up at
 * the start and down at the end by the same number of seconds. None is a priced generation
 * action — all four are deterministic audio edits over the song that already exists.
 *
 * WHAT IS STILL STAGED. Normalize still sits behind the rest of the audio-edit backend.
 * Split and its follow-up Crossfade are live now: a split lands in Version History, and
 * when the song already has a split pair there the Split section surfaces Crossfade again
 * so those two hidden backing songs can be joined back together.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  CSSProperties,
  KeyboardEvent as ReactKeyboardEvent,
  PointerEvent as ReactPointerEvent,
  ReactNode,
} from 'react';
import { Crop, Scissors } from 'lucide-react';
import { Btn, Chip, PrototypeNotice, TabBar, Tooltip } from '../ui';
import DrawerHeader from './DrawerHeader';
import { AUDIO_EDIT_CAPABILITIES, audioEditApi } from '../../api/audioEditApi';
import { formatTime, parseLength } from '../../lib/formatting';
import {
  latestSplitVersionPair,
  splitVersionPairs,
} from '../../lib/versions';
import {
  formatTimecode,
  MIN_TRIM_SECONDS,
  moveTrimEdge,
  resolveTrimRange,
  shiftTrimRange,
  trimSecondsAtFraction,
  trimSelectionPercent,
  trimmedLength,
} from '../../lib/trim';
import type { TrimRange } from '../../lib/trim';
import { seededHeights } from '../../lib/waveform';
import { c } from '../../styles/theme';
import { useStudioStore } from '../../stores/studioStore';
import type { EditCrossfadeSection, EditSection } from '../../stores/studioStore';
import type { Song, SongVersion } from '../../types/domain';

export interface TrimPanelProps {
  song: Song;
  processing?: boolean;
  onBack: () => void;
  onClose: () => void;
  editingHeadVersion?: SongVersion | null;
  onTrim?: (range: { start: number; end: number }) => Promise<unknown> | unknown;
  onCut?: (range: { start: number; end: number }) => Promise<unknown> | unknown;
  /** Splits the song into two Version History drafts and resolves with their backing ids, for Crossfade below. */
  onSplit?: (
    at: number
  ) => Promise<{ firstSongId: string; secondSongId: string } | undefined> | { firstSongId: string; secondSongId: string } | undefined;
  /** Joins the two split halves into a third, new song. */
  onCrossfade?: (firstSongId: string, secondSongId: string, seconds: number) => Promise<unknown> | unknown;
  /** Splices a silent gap into the song at a chosen point. */
  onInsertSilence?: (at: number, duration: number) => Promise<unknown> | unknown;
  /** Fades the song in at the start and out at the end, by the same number of seconds. */
  onFade?: (seconds: number) => Promise<unknown> | unknown;
  /** Normalizes the selected region to a target LUFS figure. */
  onNormalize?: (range: { start: number; end: number }, targetLufs: number) => Promise<unknown> | unknown;
}

const LABEL: CSSProperties = { fontSize: 12, color: c.text2, margin: '0 0 8px' };
const DESC: CSSProperties = { fontSize: 12, color: c.text2, margin: '0 0 14px', lineHeight: 1.55 };

const EDIT_SECTIONS: Array<{ key: EditSection; label: string }> = [
  { key: 'trim', label: 'Trim' },
  { key: 'cut', label: 'Cut' },
  { key: 'split', label: 'Split' },
  { key: 'insertSilence', label: 'Insert Silence' },
  { key: 'fade', label: 'Fade In and Out' },
  { key: 'normalize', label: 'Normalize' },
];

const CROSSFade_SECTIONS: Array<{ key: EditCrossfadeSection; label: string }> = [
  { key: 'latest', label: 'Latest pair' },
  { key: 'picker', label: 'Pick from history' },
];

/**
 * The presets, as fractions of the track.
 *
 * Fractions rather than fixed timecodes so they mean the same thing on a 30-second clip and a
 * four-minute one — "the first half" is useful at both lengths, whereas a "0:00–1:00"
 * preset is the whole song in the first case.
 */
const PRESETS: Array<{ label: string; from: number; to: number }> = [
  { label: 'First half', from: 0, to: 0.5 },
  { label: 'Second half', from: 0.5, to: 1 },
  { label: 'Middle', from: 0.25, to: 0.75 },
  { label: 'Whole song', from: 0, to: 1 },
];

export default function TrimPanel({
  song,
  processing = false,
  onBack,
  onClose,
  editingHeadVersion = null,
  onTrim,
  onCut,
  onSplit,
  onCrossfade,
  onInsertSilence,
  onFade,
  onNormalize,
}: TrimPanelProps) {
  const duration = parseLength(song.length);

  /*
    Seeded from studioStore, not created fresh, and persisted back to it on unmount below.
    This panel only renders while `drawer === 'trim'`, so plain `useState` defaults would
    reset every time the drawer switched away and back — studioStore is what makes "leave
    Edit, come back" resume the same selection, section and split point instead. The `??`
    fallbacks are the panel's original defaults, used the first time a song has no stored
    values yet.
  */
  const [startText, setStartText] = useState(() => useStudioStore.getState().editStartText ?? '0:00');
  const [endText, setEndText] = useState(() => useStudioStore.getState().editEndText ?? formatTimecode(duration));
  const [activeSection, setActiveSection] = useState<EditSection>(() => useStudioStore.getState().editActiveSection);
  const [activeAction, setActiveAction] = useState<'trim' | 'cut' | 'split' | 'crossfade' | 'insertSilence' | 'fade' | 'normalize' | null>(null);
  const [cutExecuted, setCutExecuted] = useState(() => useStudioStore.getState().editCutExecuted);
  const [splitAtText, setSplitAtText] = useState(
    () => useStudioStore.getState().editSplitAtText ?? formatTimecode(Math.round(duration / 2))
  );
  /** The two songs Split just produced, so Crossfade knows what to join. Cleared per-song. */
  const [sessionSplitParts, setSplitParts] = useState<{ firstSongId: string; secondSongId: string } | null>(
    () => useStudioStore.getState().editSplitParts
  );
  const [crossfadeSection, setCrossfadeSection] = useState<EditCrossfadeSection>(
    () => useStudioStore.getState().editCrossfadeSection
  );
  const [crossfadeSecondsText, setCrossfadeSecondsText] = useState(
    () => useStudioStore.getState().editCrossfadeSecondsText
  );
  const [crossfadeFirstVersionId, setCrossfadeFirstVersionId] = useState<string | null>(
    () => useStudioStore.getState().editCrossfadeFirstVersionId
  );
  const [crossfadeSecondVersionId, setCrossfadeSecondVersionId] = useState<string | null>(
    () => useStudioStore.getState().editCrossfadeSecondVersionId
  );
  const [fadeSecondsText, setFadeSecondsText] = useState(() => useStudioStore.getState().editFadeSecondsText);
  const [normalizeTargetLufs, setNormalizeTargetLufs] = useState(-14);

  const splitPairs = useMemo(() => splitVersionPairs(song), [song]);
  const latestSplitPair = useMemo(() => latestSplitVersionPair(song), [song]);
  const persistedSplitParts = useMemo(
    () =>
      latestSplitPair
        ? { firstSongId: latestSplitPair.first.id, secondSongId: latestSplitPair.second.id }
        : null,
    [latestSplitPair]
  );
  const effectiveSplitParts = sessionSplitParts ?? persistedSplitParts;
  const selectedCrossfadePair = useMemo(
    () =>
      splitPairs.find(
        (pair) => pair.first.id === crossfadeFirstVersionId && pair.second.id === crossfadeSecondVersionId
      ) ?? null,
    [crossfadeFirstVersionId, crossfadeSecondVersionId, splitPairs]
  );
  const crossfadeSeconds = Number(crossfadeSecondsText);
  const crossfadeSecondsValid = Number.isFinite(crossfadeSeconds) && crossfadeSeconds > 0;

  const { range, error, field } = resolveTrimRange(startText, endText, duration);
  const splitCapability = AUDIO_EDIT_CAPABILITIES.split;
  const fadeCapability = AUDIO_EDIT_CAPABILITIES.fade;
  const crossfadeCapability = AUDIO_EDIT_CAPABILITIES.crossfade;
  const insertSilenceCapability = AUDIO_EDIT_CAPABILITIES.insertSilence;

  const canTrim = audioEditApi.supports('trim');
  const canCut = audioEditApi.supports('cut');
  const canSplit = audioEditApi.supports('split');
  const canCrossfade = audioEditApi.supports('crossfade');
  const canInsertSilence = audioEditApi.supports('insertSilence');
  const canFade = audioEditApi.supports('fade');
  const fadeSeconds = Number(fadeSecondsText);
  const fadeValid = Number.isFinite(fadeSeconds) && fadeSeconds > 0 && fadeSeconds * 2 < duration;

  // Split's result belongs to the songs it produced, not to this one — once the workspace
  // moves to a different song, stale ids from a previous split must not linger.
  useEffect(() => {
    setSplitParts(null);
  }, [song.id]);

  // The picker only makes sense while its chosen version ids still exist as valid split rows.
  // If Version History changes underneath it, clear the stale side rather than keeping an id
  // the selects can no longer represent.
  useEffect(() => {
    if (splitPairs.length <= 1 && crossfadeSection !== 'latest') {
      setCrossfadeSection('latest');
    }
    if (crossfadeFirstVersionId && !splitPairs.some((pair) => pair.first.id === crossfadeFirstVersionId)) {
      setCrossfadeFirstVersionId(null);
    }
    if (crossfadeSecondVersionId && !splitPairs.some((pair) => pair.second.id === crossfadeSecondVersionId)) {
      setCrossfadeSecondVersionId(null);
    }
  }, [crossfadeFirstVersionId, crossfadeSecondVersionId, crossfadeSection, splitPairs]);

  /*
    Mirrors every render's values into a ref so the unmount effect below reads the LATEST
    state rather than whatever was captured when the effect was created (an empty
    dependency array closure otherwise only ever sees the first render's values).
  */
  const editFormRef = useRef({
    startText,
    endText,
    activeSection,
    cutExecuted,
    splitAtText,
    sessionSplitParts,
    crossfadeSection,
    crossfadeSecondsText,
    crossfadeFirstVersionId,
    crossfadeSecondVersionId,
    fadeSecondsText,
  });
  editFormRef.current = {
    startText,
    endText,
    activeSection,
    cutExecuted,
    splitAtText,
    sessionSplitParts,
    crossfadeSection,
    crossfadeSecondsText,
    crossfadeFirstVersionId,
    crossfadeSecondVersionId,
    fadeSecondsText,
  };

  // Persists this panel's UI state to studioStore the moment it unmounts — leaving the Edit
  // drawer for another one, or closing the Studio outright — so the NEXT mount's lazy
  // `useState` initializers above pick it back up instead of resetting to defaults. Written
  // once here rather than on every keystroke/drag frame, since nothing else reads it live.
  useEffect(() => {
    return () => {
      const snapshot = editFormRef.current;
      useStudioStore.getState().setEditForm({
        editActiveSection: snapshot.activeSection,
        editStartText: snapshot.startText,
        editEndText: snapshot.endText,
        editSplitAtText: snapshot.splitAtText,
        editCrossfadeSection: snapshot.crossfadeSection,
        editCrossfadeSecondsText: snapshot.crossfadeSecondsText,
        editCutExecuted: snapshot.cutExecuted,
        editSplitParts: snapshot.sessionSplitParts,
        editCrossfadeFirstVersionId: snapshot.crossfadeFirstVersionId,
        editCrossfadeSecondVersionId: snapshot.crossfadeSecondVersionId,
        editFadeSecondsText: snapshot.fadeSecondsText,
      });
    };
  }, []);

  // Where the two handles and the lit body sit, as percentages of the lane. Computed once and
  // shared by all three, so a handle and the edge of the body it sits on can never be drawn a
  // rounding error apart. Null while the fields do not describe a valid range.
  const selection = range ? trimSelectionPercent(range, duration) : null;
  const selectedLength = range ? trimmedLength(range) : null;
  const remainingAfterCut = range ? Math.max(0, duration - trimmedLength(range)) : null;

  const laneRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<
    | { mode: 'start' | 'end' | 'body'; rangeAtStart: TrimRange; grabSeconds: number }
    | { mode: 'split'; rangeAtStart?: TrimRange; grabSeconds: number }
    | null
  >(null);

  useEffect(() => {
    if (!processing) setActiveAction(null);
  }, [processing]);

  /** Turns a pointer's x into whole seconds on the track, or null if the lane is unmeasured. */
  const pointerSeconds = useCallback(
    (clientX: number): number | null => {
      const rect = laneRef.current?.getBoundingClientRect();
      if (!rect || !(rect.width > 0)) return null;
      return trimSecondsAtFraction((clientX - rect.left) / rect.width, duration);
    },
    [duration]
  );

  /** Applies the current drag at pointer position `clientX`, writing both fields. */
  const applyDrag = useCallback(
    (clientX: number) => {
      const drag = dragRef.current;
      if (!drag) return;
      const seconds = pointerSeconds(clientX);
      if (seconds === null) return;
      if (drag.mode === 'split') {
        const clamped = Math.max(0, Math.min(duration, Math.round(seconds)));
        setSplitAtText(formatTimecode(clamped));
        return;
      }
      const next =
        drag.mode === 'body'
          ? shiftTrimRange(drag.rangeAtStart as TrimRange, seconds - drag.grabSeconds, duration)
          : moveTrimEdge(drag.rangeAtStart as TrimRange, drag.mode as 'start' | 'end', seconds, duration);
      setStartText(formatTimecode(next.start));
      setEndText(formatTimecode(next.end));
    },
    [duration, pointerSeconds]
  );

  useEffect(() => {
    function onMove(e: PointerEvent) {
      if (dragRef.current) applyDrag(e.clientX);
    }
    function onUp() {
      dragRef.current = null;
    }
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [applyDrag]);

  /** Starts a drag from a handle or from the selection body. */
  function beginDrag(mode: 'start' | 'end' | 'body', e: ReactPointerEvent) {
    if (!range || processing || e.button !== 0) return;
    e.preventDefault();
    dragRef.current = { mode, rangeAtStart: range, grabSeconds: pointerSeconds(e.clientX) ?? 0 };
  }

  function beginSplitDrag(e: ReactPointerEvent) {
    if (processing || e.button !== 0) return;
    e.preventDefault();
    dragRef.current = { mode: 'split', rangeAtStart: range ?? { start: 0, end: duration }, grabSeconds: pointerSeconds(e.clientX) ?? 0 };
  }

  /** Arrow / Home / End on a handle, so the selection is adjustable without a pointer. */
  function nudgeEdge(edge: 'start' | 'end', e: ReactKeyboardEvent) {
    if (!range || processing) return;
    const current = edge === 'start' ? range.start : range.end;
    const step = e.shiftKey ? 5 : 1;
    let target: number;
    switch (e.key) {
      case 'ArrowLeft':
      case 'ArrowDown':
        target = current - step;
        break;
      case 'ArrowRight':
      case 'ArrowUp':
        target = current + step;
        break;
      case 'Home':
        target = edge === 'start' ? 0 : range.start + MIN_TRIM_SECONDS;
        break;
      case 'End':
        target = edge === 'start' ? range.end - MIN_TRIM_SECONDS : duration;
        break;
      default:
        return;
    }
    e.preventDefault();
    const next = moveTrimEdge(range, edge, target, duration);
    setStartText(formatTimecode(next.start));
    setEndText(formatTimecode(next.end));
  }

  async function performTrim() {
    if (!range || !onTrim) return;
    setActiveAction('trim');
    try {
      await onTrim(range);
    } catch {
      // The route hook already reports the failure. Leaving this silent here prevents a second,
      // unhandled rejection path in the panel itself.
    }
  }

  async function performCut() {
    if (!range || !onCut) return;
    setActiveAction('cut');
    try {
      await onCut(range);
      setCutExecuted(true);
      const nextDuration = Math.max(0, duration - trimmedLength(range));
      setStartText('0:00');
      setEndText(formatTimecode(nextDuration));
    } catch {
      // The hook toasts the error already; the panel only decides whether to reveal the follow-up
      // section, which it must not do on a failed cut.
    }
  }

  async function performSplit() {
    if (!onSplit) return;
    // parse the text as seconds
    const seconds = parseLength(splitAtText);
    if (!Number.isFinite(seconds) || seconds <= 0 || seconds >= duration) return;
    setActiveAction('split');
    try {
      const result = await onSplit(seconds);
      if (result) setSplitParts(result);
    } catch {
      // Handled by caller
    }
  }

  async function performCrossfade() {
    if (!onCrossfade || !effectiveSplitParts || !crossfadeSecondsValid) return;
    setActiveAction('crossfade');
    try {
      await onCrossfade(effectiveSplitParts.firstSongId, effectiveSplitParts.secondSongId, crossfadeSeconds);
    } catch {
      // Handled by caller
    }
  }

  async function performSelectedCrossfade() {
    if (!onCrossfade || !selectedCrossfadePair || !crossfadeSecondsValid) return;
    setActiveAction('crossfade');
    try {
      await onCrossfade(selectedCrossfadePair.first.id, selectedCrossfadePair.second.id, crossfadeSeconds);
    } catch {
      // Handled by caller
    }
  }

  async function performInsertSilence() {
    if (!range || !onInsertSilence) return;
    setActiveAction('insertSilence');
    try {
      await onInsertSilence(range.start, trimmedLength(range));
    } catch {
      // Handled by caller
    }
  }

  async function performFade() {
    if (!onFade) return;
    const seconds = Number(fadeSecondsText);
    if (!Number.isFinite(seconds) || seconds <= 0) return;
    setActiveAction('fade');
    try {
      await onFade(seconds);
    } catch {
      // Handled by caller
    }
  }

  async function performNormalize() {
    if (!range || !onNormalize) return;
    setActiveAction('normalize');
    try {
      await onNormalize(range, normalizeTargetLufs);
    } catch {
      // Handled by caller
    }
  }

  useEffect(() => {
    // Seed the splitAt field from the current selection only when entering Split mode.
    // Deliberately depends on `activeSection` alone — `range` is a fresh object every
    // render (see resolveTrimRange), so depending on it here would re-run this effect,
    // and therefore reset splitAtText, on every keystroke and every drag frame.
    if (activeSection === 'split' && range) {
      const center = Math.round((range.start + range.end) / 2);
      setSplitAtText(formatTimecode(center));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection]);

  /** One timecode field. Marked invalid only when the error belongs to it. */
  function timeField(
    id: string,
    label: string,
    value: string,
    onChange: (next: string) => void,
    invalid: boolean
  ) {
    return (
      <div style={{ flex: '1 1 120px', minWidth: 0 }}>
        <label
          htmlFor={id}
          style={{ fontSize: 12, color: c.text2, display: 'block', margin: '0 0 6px' }}
        >
          {label}
        </label>
        <input
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onPointerDown={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          inputMode="numeric"
          placeholder="0:00"
          aria-invalid={invalid || undefined}
          aria-describedby={invalid ? 'studio-trim-error' : undefined}
          className="lyx-tool-field"
          style={{
            width: '100%',
            background: c.surface2,
            border: `1px solid ${invalid ? c.danger : c.border}`,
            borderRadius: 8,
            padding: '8px 10px',
            color: c.text1,
            fontFamily: 'IBM Plex Mono',
          }}
        />
      </div>
    );
  }

  function unavailableButton(
    label: string,
    unavailableLabel: string,
    reason: string,
    icon?: ReactNode,
    variant: 'primary' | 'secondary' = 'primary'
  ) {
    return (
      <Tooltip label={reason}>
        <span style={{ display: 'block', width: '100%' }}>
          <Btn full variant={variant} disabled aria-label={`${label} — ${unavailableLabel}`}>
            {icon}
            {label}
          </Btn>
        </span>
      </Tooltip>
    );
  }

  function renderActiveSection() {
    switch (activeSection) {
      case 'trim':
        return (
          <>
            <p style={DESC}>Keep only the selected section and replace the song with that excerpt.</p>
            <Tooltip
              label={
                !canTrim
                  ? 'Not available without the Studio audio-edit backend.'
                  : range === null
                    ? 'Choose a valid edit range first.'
                    : 'Keep only the selected section.'
              }
            >
              <span style={{ display: 'block', width: '100%' }}>
                <Btn
                  full
                  disabled={!canTrim || range === null || processing}
                  aria-label={!canTrim ? 'Trim selection — not available yet' : 'Trim selection'}
                  onClick={range ? () => void performTrim() : undefined}
                >
                  <Crop size={13} />
                  {processing && activeAction === 'trim' ? 'Trimming…' : 'Trim selection'}
                </Btn>
              </span>
            </Tooltip>
          </>
        );

      case 'cut':
        return (
          <>
            <p style={DESC}>Remove the selected section and join what remains into one shorter song.</p>
            <Tooltip
              label={
                !canCut
                  ? 'Not available without the Studio audio-edit backend.'
                  : range === null
                    ? 'Choose a valid edit range first.'
                    : 'Remove the selected section.'
              }
            >
              <span style={{ display: 'block', width: '100%' }}>
                <Btn
                  full
                  variant="secondary"
                  disabled={!canCut || range === null || processing}
                  aria-label={!canCut ? 'Cut selection — not available yet' : 'Cut selection'}
                  onClick={range ? () => void performCut() : undefined}
                >
                  <Scissors size={13} />
                  {processing && activeAction === 'cut' ? 'Cutting…' : 'Cut selection'}
                </Btn>
              </span>
            </Tooltip>

            {cutExecuted && (
              <div style={{ marginTop: 14 }}>
                <p style={{ ...LABEL, marginBottom: 6 }}>Cross fade</p>
                <p style={DESC}>
                  Now that a cut exists, this is where the join could be softened. It stays nested
                  here rather than appearing as a top-level edit of its own.
                </p>
                {unavailableButton(
                  'Apply cross fade',
                  'not available yet',
                  crossfadeCapability.missing,
                  undefined,
                  'secondary'
                )}
              </div>
            )}
          </>
        );

      case 'split':
        return (
          <>
            <p style={DESC}>
              Create two songs by splitting at a single point. Enter the time below and press
              Split.
            </p>
            <div style={{ display: 'flex', gap: 10, marginBottom: 12, alignItems: 'flex-end' }}>
              {timeField(
                'studio-split-at',
                'Split at',
                splitAtText,
                setSplitAtText,
                false
              )}
              <Tooltip
                label={
                  !canSplit
                    ? splitCapability.missing
                    : parseLength(splitAtText) > 0 && parseLength(splitAtText) < duration
                      ? 'Split into two songs at this point.'
                      : 'Choose a split point inside the song.'
                }
              >
                <span style={{ display: 'block', width: 160 }}>
                  <Btn
                    full
                    variant="secondary"
                    disabled={!canSplit || processing}
                    aria-label={!canSplit ? 'Split — not available yet' : 'Split'}
                    onClick={() => void performSplit()}
                  >
                    {processing && activeAction === 'split' ? 'Splitting…' : 'Split'}
                  </Btn>
                </span>
              </Tooltip>
            </div>

            {effectiveSplitParts && (
              <div style={{ marginTop: 14 }}>
                <p style={{ ...LABEL, marginBottom: 6 }}>Crossfade</p>
                <p style={DESC}>
                  Join the two split songs back together with a crossfade at the seam. This
                  creates a third, new song, which becomes the one open here.
                </p>
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
                  <div style={{ flex: '1 1 120px', minWidth: 0 }}>
                    <label
                      htmlFor="studio-crossfade-seconds"
                      style={{ fontSize: 12, color: c.text2, display: 'block', margin: '0 0 6px' }}
                    >
                      Crossfade (seconds)
                    </label>
                    <input
                      id="studio-crossfade-seconds"
                      type="number"
                      min={0}
                      step="0.5"
                      value={crossfadeSecondsText}
                      onChange={(e) => setCrossfadeSecondsText(e.target.value)}
                      style={{
                        width: '100%',
                        boxSizing: 'border-box',
                        padding: '8px 10px',
                        borderRadius: 8,
                        border: `1px solid ${c.border}`,
                        background: c.surface2,
                        color: c.text1,
                        fontSize: 13,
                      }}
                    />
                  </div>
                  <Tooltip
                    label={
                      !canCrossfade
                        ? crossfadeCapability.missing
                        : crossfadeSecondsValid
                          ? 'Crossfade the two split songs into a new one.'
                          : 'Choose a crossfade length.'
                    }
                  >
                    <span style={{ display: 'block', width: 160 }}>
                      <Btn
                        full
                        variant="secondary"
                        disabled={!canCrossfade || processing || !crossfadeSecondsValid}
                        aria-label={!canCrossfade ? 'Crossfade — not available yet' : 'Crossfade'}
                        onClick={() => void performCrossfade()}
                      >
                        {processing && activeAction === 'crossfade' ? 'Crossfading…' : 'Crossfade'}
                      </Btn>
                    </span>
                  </Tooltip>
                </div>

                {splitPairs.length > 1 && (
                  <div
                    style={{
                      marginTop: 14,
                      paddingTop: 14,
                      borderTop: `1px solid ${c.border}`,
                      display: 'grid',
                      gap: 12,
                    }}
                  >
                    <div>
                      <p style={{ ...LABEL, marginBottom: 6 }}>Crossfade subsection</p>
                      <p style={{ ...DESC, marginBottom: 0 }}>
                        Choose whether to rejoin the latest split pair automatically or pick a
                        different split pair from Version History.
                      </p>
                    </div>
                    <TabBar
                      tabs={CROSSFade_SECTIONS}
                      active={crossfadeSection}
                      onSelect={setCrossfadeSection}
                      label="Crossfade subsection"
                      marginBottom={0}
                    />

                    {crossfadeSection === 'latest' && (
                      <p style={{ ...DESC, marginBottom: 0 }}>
                        Use the latest valid split pair already detected from Version History.
                      </p>
                    )}

                    {crossfadeSection === 'picker' && (
                      <>
                        <div>
                          <p style={{ ...LABEL, marginBottom: 6 }}>Crossfade another split pair</p>
                          <p style={{ ...DESC, marginBottom: 0 }}>
                            Pick two split-derived versions from Version History to crossfade a
                            different seam into a new song.
                          </p>
                        </div>
                        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                          <div style={{ flex: '1 1 220px', minWidth: 0 }}>
                            <label
                              htmlFor="studio-crossfade-first-version"
                              style={{ fontSize: 12, color: c.text2, display: 'block', margin: '0 0 6px' }}
                            >
                              First split version
                            </label>
                            <select
                              id="studio-crossfade-first-version"
                              value={crossfadeFirstVersionId ?? ''}
                              onChange={(e) => setCrossfadeFirstVersionId(e.target.value || null)}
                              className="lyx-tool-field"
                              style={{ width: '100%' }}
                            >
                              <option value="">Choose Part 1</option>
                              {splitPairs.map((pair) => (
                                <option key={pair.first.id} value={pair.first.id}>
                                  {pair.first.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div style={{ flex: '1 1 220px', minWidth: 0 }}>
                            <label
                              htmlFor="studio-crossfade-second-version"
                              style={{ fontSize: 12, color: c.text2, display: 'block', margin: '0 0 6px' }}
                            >
                              Second split version
                            </label>
                            <select
                              id="studio-crossfade-second-version"
                              value={crossfadeSecondVersionId ?? ''}
                              onChange={(e) => setCrossfadeSecondVersionId(e.target.value || null)}
                              className="lyx-tool-field"
                              style={{ width: '100%' }}
                            >
                              <option value="">Choose Part 2</option>
                              {splitPairs.map((pair) => (
                                <option key={pair.second.id} value={pair.second.id}>
                                  {pair.second.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <Tooltip
                          label={
                            !canCrossfade
                              ? crossfadeCapability.missing
                              : !crossfadeSecondsValid
                                ? 'Choose a crossfade length.'
                                : selectedCrossfadePair
                                  ? 'Crossfade the selected split versions into a new song.'
                                  : 'Choose two versions from the same split pair.'
                          }
                        >
                          <span style={{ display: 'block', width: '100%' }}>
                            <Btn
                              full
                              variant="secondary"
                              disabled={!canCrossfade || processing || !crossfadeSecondsValid || !selectedCrossfadePair}
                              aria-label={
                                !canCrossfade
                                  ? 'Crossfade selected versions — not available yet'
                                  : 'Crossfade selected versions'
                              }
                              onClick={() => void performSelectedCrossfade()}
                            >
                              {processing && activeAction === 'crossfade'
                                ? 'Crossfading…'
                                : 'Crossfade selected versions'}
                            </Btn>
                          </span>
                        </Tooltip>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </>
        );

      case 'insertSilence':
        return (
          <>
            <p style={DESC}>
              Insert a silent gap the length of the selected section, at its start — the same
              range Trim and Cut use above.
            </p>
            <Tooltip
              label={
                !canInsertSilence
                  ? insertSilenceCapability.missing
                  : range === null
                    ? 'Choose a valid edit range first.'
                    : 'Insert a silent gap at the selection.'
              }
            >
              <span style={{ display: 'block', width: '100%' }}>
                <Btn
                  full
                  variant="secondary"
                  disabled={!canInsertSilence || range === null || processing}
                  aria-label={!canInsertSilence ? 'Insert silence — not available yet' : 'Insert silence'}
                  onClick={range ? () => void performInsertSilence() : undefined}
                >
                  {processing && activeAction === 'insertSilence' ? 'Inserting…' : 'Insert silence'}
                </Btn>
              </span>
            </Tooltip>
          </>
        );


      case 'fade':
        return (
          <>
            <p style={DESC}>Fade the song in at the start and out at the end, by the same amount.</p>
            <div style={{ marginBottom: 12 }}>
              <label
                htmlFor="studio-fade-seconds"
                style={{ fontSize: 12, color: c.text2, display: 'block', margin: '0 0 6px' }}
              >
                Fade (seconds)
              </label>
              <input
                id="studio-fade-seconds"
                type="number"
                min={0}
                step="0.5"
                value={fadeSecondsText}
                onChange={(e) => setFadeSecondsText(e.target.value)}
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  padding: '8px 10px',
                  borderRadius: 8,
                  border: `1px solid ${c.border}`,
                  background: c.surface2,
                  color: c.text1,
                  fontSize: 13,
                }}
              />
            </div>
            <Tooltip
              label={
                !canFade
                  ? fadeCapability.missing
                  : !fadeValid
                    ? 'Choose a fade length shorter than half the song.'
                    : 'Fade the song in at the start and out at the end.'
              }
            >
              <span style={{ display: 'block', width: '100%' }}>
                <Btn
                  full
                  variant="secondary"
                  disabled={!canFade || !fadeValid || processing}
                  aria-label={!canFade ? 'Apply fades — not available yet' : 'Apply fades'}
                  onClick={fadeValid ? () => void performFade() : undefined}
                >
                  {processing && activeAction === 'fade' ? 'Fading…' : 'Apply fades'}
                </Btn>
              </span>
            </Tooltip>
          </>
        );

      case 'normalize':
        return (
          <>
            <p style={DESC}>Match the selected section to a consistent loudness target.</p>
            <div style={{ marginBottom: 12 }}>
              <label
                htmlFor="studio-normalize-lufs"
                style={{ fontSize: 12, color: c.text2, display: 'block', margin: '0 0 6px' }}
              >
                Target loudness
              </label>
              <div className="lyx-tool-row" style={{ marginBottom: 8 }}>
                <input
                  id="studio-normalize-lufs"
                  type="range"
                  min={-24}
                  max={-6}
                  step={1}
                  value={normalizeTargetLufs}
                  onChange={(e) => setNormalizeTargetLufs(Number(e.target.value))}
                  aria-label="Custom loudness target in LUFS"
                  aria-valuetext={`${normalizeTargetLufs} LUFS`}
                  className="lyx-tool-slider"
                />
                <span
                  style={{
                    fontSize: 12,
                    fontFamily: 'IBM Plex Mono',
                    color: c.text1,
                    width: 78,
                    textAlign: 'center',
                    flexShrink: 0,
                  }}
                >
                  {normalizeTargetLufs} LUFS
                </span>
              </div>
            </div>
            <Tooltip
              label={
                !range
                  ? 'Choose a valid edit range first.'
                  : 'Normalize only the selected section to this LUFS target.'
              }
            >
              <span style={{ display: 'block', width: '100%' }}>
                <Btn
                  full
                  variant="secondary"
                  disabled={!range || processing}
                  aria-label="Normalize section"
                  onClick={range ? () => void performNormalize() : undefined}
                >
                  {processing && activeAction === 'normalize' ? 'Normalizing…' : 'Normalize section'}
                </Btn>
              </span>
            </Tooltip>
          </>
        );
    }
  }

  return (
    <div className="lyx-tool-panel">
      <DrawerHeader title="Edit" onBack={onBack} onClose={onClose} />
      {activeSection !== 'fade' && (
        <p style={DESC}>
          Choose the section of &quot;{song.title}&quot; to edit — type the times below, pick a preset,
          or drag the handles on the waveform.
        </p>
      )}

      {/* ---- the interactive selection over the waveform ---- */}
      {activeSection !== 'fade' && (
      <div
        ref={laneRef}
        className="lyx-trim-lane"
        style={{ background: c.surface1, border: `1px solid ${c.border}` }}
      >
        <div className="lyx-trim-wave" aria-hidden="true">
          {seededHeights(song.id, 72).map((h, i) => (
            <div
              key={i}
              style={{
                flex: 1,
                minWidth: 1,
                height: `${Math.round((h / 26) * 100)}%`,
                borderRadius: 1,
                background: c.borderStrong,
              }}
            />
          ))}
        </div>

        {activeSection === 'split' ? (
          // Single split marker synced with splitAtText
          (() => {
            const seconds = parseLength(splitAtText) || 0;
            const clamped = Math.max(0, Math.min(duration, Number.isFinite(seconds) ? seconds : 0));
            const left = (clamped / duration) * 100 || 0;
            return (
              <>
                <div
                  className="lyx-trim-handle"
                  role="slider"
                  tabIndex={processing ? -1 : 0}
                  aria-label="Split at"
                  aria-orientation="horizontal"
                  aria-valuemin={0}
                  aria-valuemax={duration}
                  aria-valuenow={clamped}
                  aria-valuetext={formatTimecode(clamped)}
                  onPointerDown={(e) => beginSplitDrag(e)}
                  onKeyDown={(e) => {
                    if (processing) return;
                    const step = e.shiftKey ? 5 : 1;
                    if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
                      e.preventDefault();
                      setSplitAtText(formatTimecode(Math.max(0, clamped - step)));
                    } else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
                      e.preventDefault();
                      setSplitAtText(formatTimecode(Math.min(duration, clamped + step)));
                    }
                  }}
                  style={{ left: `${left}%`, color: c.accentFill, pointerEvents: 'auto' }}
                />
                <div
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    left: `${left}%`,
                    top: 0,
                    bottom: 0,
                    width: 2,
                    background: c.accentFill,
                    transform: 'translateX(-50%)',
                    pointerEvents: 'none',
                  }}
                />
              </>
            );
          })()
        ) : (
          range &&
          selection && (
            <>
              <div
                className="lyx-trim-selection"
                aria-hidden="true"
                onPointerDown={(e) => beginDrag('body', e)}
                style={{
                  left: `${selection.left}%`,
                  width: `${selection.width}%`,
                  background: c.accentSoft,
                  borderLeft: `2px solid ${c.accentFill}`,
                  borderRight: `2px solid ${c.accentFill}`,
                }}
              />
              <div
                className="lyx-trim-handle"
                role="slider"
                tabIndex={processing ? -1 : 0}
                aria-label="Edit start"
                aria-orientation="horizontal"
                aria-valuemin={0}
                aria-valuemax={range.end - MIN_TRIM_SECONDS}
                aria-valuenow={range.start}
                aria-valuetext={formatTimecode(range.start)}
                onPointerDown={(e) => beginDrag('start', e)}
                onKeyDown={(e) => nudgeEdge('start', e)}
                style={{ left: `${selection.left}%`, color: c.accentFill }}
              />
              <div
                className="lyx-trim-handle"
                role="slider"
                tabIndex={processing ? -1 : 0}
                aria-label="Edit end"
                aria-orientation="horizontal"
                aria-valuemin={range.start + MIN_TRIM_SECONDS}
                aria-valuemax={duration}
                aria-valuenow={range.end}
                aria-valuetext={formatTimecode(range.end)}
                onPointerDown={(e) => beginDrag('end', e)}
                onKeyDown={(e) => nudgeEdge('end', e)}
                style={{ left: `${selection.left + selection.width}%`, color: c.accentFill }}
              />
            </>
          )
        )}
      </div>
      )}

      <div style={{ marginTop: 10 }}>
        <TabBar
          tabs={EDIT_SECTIONS}
          active={activeSection}
          onSelect={setActiveSection}
          label="Edit sections"
          scrollable
          marginBottom={12}
        />
      </div>

      {editingHeadVersion && (
        <PrototypeNotice style={{ marginTop: 0, marginBottom: 12 }}>
          Editing from {editingHeadVersion.label}. New edits will branch from this loaded head until another version is explicitly loaded.
        </PrototypeNotice>
      )}

      {activeSection !== 'split' && activeSection !== 'fade' && (
        <>
          <p style={{ ...LABEL, marginTop: 0 }}>Selected section</p>
          <div style={{ display: 'flex', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
            {timeField('studio-trim-start', 'Start', startText, setStartText, field === 'start' && error !== null)}
            {timeField('studio-trim-end', 'End', endText, setEndText, field === 'end' && error !== null)}
          </div>

          <p
            id="studio-trim-error"
            role="status"
            aria-live="polite"
            style={{
              fontSize: 11.5,
              margin: '0 0 14px',
              minHeight: 16,
              color: error ? c.danger : c.text2,
              fontFamily: error ? 'Inter' : 'IBM Plex Mono',
            }}
          >
            {error ??
              (range && selectedLength !== null && remainingAfterCut !== null
                ? activeSection === 'normalize'
                  ? `Normalizing ${formatTime(selectedLength)} of ${formatTime(duration)}`
                  : `Selected ${formatTime(selectedLength)} of ${formatTime(duration)} · Cut would leave ${formatTime(
                      remainingAfterCut
                    )}`
                : '')}
          </p>
        </>
      )}

      {activeSection !== 'split' && activeSection !== 'fade' && (
        <>
          <p style={LABEL}>Presets</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
            {PRESETS.map((preset) => {
              const from = Math.round(duration * preset.from);
              const to = Math.round(duration * preset.to);
              return (
                <Chip
                  key={preset.label}
                  label={preset.label}
                  active={range?.start === from && range?.end === to}
                  onClick={() => {
                    setStartText(formatTimecode(from));
                    setEndText(formatTimecode(to));
                  }}
                />
              );
            })}
          </div>
        </>
      )}

      {renderActiveSection()}
    </div>
  );
}
